/** Every call goes through here, so auth and error shape are handled once. */
export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T;
}

/**
 * Where the API lives.
 *
 * In the browser it is the same origin. Inside the Android shell the page is
 * served from the APK on a capacitor:// or https://localhost origin, so a
 * relative /api/... would resolve to the bundle rather than the server.
 */
const NATIVE = typeof window !== "undefined" &&
  (window.location.protocol === "capacitor:" ||
   window.location.hostname === "localhost" ||
   /Orbit\/\d/.test(navigator.userAgent));

export const API_BASE = NATIVE ? "https://play.cyctems.org" : "";

/**
 * The session token, for the Android shell.
 *
 * In a browser the session is a cookie and this is never used. Inside the
 * WebView the app is served from https://localhost, so every API call is
 * cross-site and a SameSite=Lax cookie is never sent — the server hands the
 * same token back on login and it travels as a bearer header instead.
 *
 * Kept in localStorage rather than memory so the session survives the app being
 * closed, which for a scorer between two innings is routine.
 */
const TOKEN_KEY = "orbit.session";

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode */ }
}

export function getToken(): string | null {
  if (!NATIVE) return null;
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

/** WebSocket origin. Same reasoning as API_BASE. */
export function wsBase(): string {
  if (NATIVE) return "wss://play.cyctems.org";
  return `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}`;
}

/**
 * What went wrong, in words.
 *
 * The API answers with two shapes: `{ error: { code, message } }` when the
 * rules engine refuses a delivery, and `{ error: "a sentence" }` from the route
 * guards — permissions, a missing match, a malformed body. The scorer only
 * understood the first, so anything from the second arrived as
 * "That delivery was refused", which tells a scorer at a ground precisely
 * nothing.
 */
export function errorText(data: unknown, status?: number): string {
  const d = data as { error?: unknown } | null;
  const e = d?.error;

  if (typeof e === "string" && e.trim()) return e;
  if (e && typeof e === "object") {
    const o = e as { message?: unknown; code?: unknown };
    if (typeof o.message === "string" && o.message.trim()) return o.message;
    if (typeof o.code === "string") return `Refused: ${o.code.toLowerCase().replace(/_/g, " ")}.`;
  }

  // Nothing usable came back. Say what is actually known rather than inventing
  // a reason — a status code is at least something to report.
  if (status === 401 || status === 403) return "You are not allowed to do that.";
  if (status === 404) return "That match no longer exists.";
  if (status && status >= 500) return "The server had a problem. Try again.";
  return status ? `That was refused (${status}).` : "That was refused.";
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<ApiResult<T>> {
  path = path.startsWith("/api/") ? API_BASE + path : path;

  const token = getToken();
  if (token) {
    init.headers = { ...(init.headers as Record<string, string> | undefined),
                     authorization: `Bearer ${token}` };
  }
  const { json, ...rest } = init;
  const opts: RequestInit = { credentials: "same-origin", ...rest };
  if (json !== undefined) {
    opts.method = opts.method ?? "POST";
    opts.headers = { "content-type": "application/json", ...(opts.headers ?? {}) };
    opts.body = JSON.stringify(json);
  }
  const res = await fetch(path, opts);
  const data = (await res.json().catch(() => ({}))) as T;
  return { ok: res.ok, status: res.status, data };
}

export interface User { id: string; email: string; displayName: string }
export interface Club { id: string; name: string; slug: string; role: string }
export interface Team { id: string; name: string; shortName: string; colour?: string | null }
export interface Person {
  personId: string; playerId: string | null; displayName: string;
  battingHand?: string | null; bowlingStyle?: string | null;
}
export interface SquadMember { playerId: string; personId: string; displayName: string; fromDate: string }
export interface Spell { teamId: string; name: string; shortName: string; fromDate: string; toDate: string | null }
